import { PageWrapper, SaveAndCancel, StickyActionsHeader } from "@/components";
import { RoleForm } from "@/containers/Project/components/ProjectTeam/styled";
import {
  RegionDisplayTab,
  TeamCopyModal,
} from "@/containers/Project/components/ProjectTeam";
import { ProjectViewersTab } from "@/containers/Project/components/ProjectTeam/components/ProjectViewersTab";
import { GUTTER_SIZE, PROJECT_STATUS, ProjectUserPermissions } from "@/enums";
import { getUpdatedProjectTeamRoles, routes } from "@/helpers";
import { IProjectTeamRole, ITranslation } from "@/interfaces";
import { useProjectContext } from "@/providers";
import {
  useGetFormLookupsQuery,
  useUpdateProjectTeamMutation,
} from "@/services";
import { Button, Card, Col, Form, Modal, Row, Space, Spin, Tabs } from "antd";
import _ from "lodash";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

export const ProjectTeamPage = () => {
  const intl = useIntl();
  const navigate = useNavigate();

  const { projectId } = useParams();
  const { canUser, project, setProject, refetchProject } = useProjectContext();

  const readOnlyMode = project.status === PROJECT_STATUS.project.canceled.id;
  const [form] = Form.useForm();

  const { data: forms } = useGetFormLookupsQuery();
  const [updateProjectTeam, { isLoading: updatingProjectTeam }] =
    useUpdateProjectTeamMutation();

  const [regions, setRegions] = useState<{ [region: string]: ITranslation }>();
  const [projectTeamRoles, setProjectTeamRoles] =
    useState<IProjectTeamRole[]>();
  const [projectViewers, setProjectViewers] = useState([]);
  const [defaultTab, setDefaultTab] = useState<string>();

  const [showCopyModal, setShowCopyModal] = useState<boolean>(false);
  const { confirm } = Modal;
  useEffect(() => {
    if (!forms?.intake) return;

    setRegions(forms.intake.region);
  }, [forms?.intake]);

  useEffect(() => {
    if (project?.projectRoles) {
      setProjectTeamRoles(project.projectRoles);
    }
  }, [project?.projectRoles]);

  useEffect(() => {
    if (!project?.projectViewers) return;

    setProjectViewers(project.projectViewers);
    if (!regions) return;

    form.setFieldValue("projectViewers", project.projectViewers);
  }, [form, project?.projectViewers, regions]);

  useEffect(() => {
    if (!regions) return;

    setDefaultTab(Object.keys(regions)[0]);
  }, [regions]);

  const saveProjectTeam = (): void => {
    const fieldsValue = form.getFieldsValue(true);
    const fields = Object.keys(fieldsValue).map((key) => fieldsValue[key]);

    const roles =
      getUpdatedProjectTeamRoles(fieldsValue, project.projectRoles) || [];

    const updatedProjectTeam = {
      projectRoles: roles,
      projectViewers: fieldsValue.projectViewers || [],
    };
    setProjectTeamRoles(roles);
    setProjectViewers(updatedProjectTeam.projectViewers);

    updateProjectTeam({ id: project.id, projectTeam: updatedProjectTeam })
      .unwrap()
      .then((project) => {
        fields
          .filter((field) => field.isNewRole)
          .forEach((role) => {
            form.setFieldsValue({
              [role.id]: {
                ...role,
                isNewRole: false,
              },
            });
          });
        setProject(project);
        toast.success(intl.formatMessage({ id: "general.toastChangesSaved" }));
      })
      .catch(() => {
        toast.error(intl.formatMessage({ id: "project.projectTeamSaveError" }));
      });
  };

  const validateUnsavedChangesAndSetShowCopyModal = () => {
    const fieldsValue = form.getFieldsValue(true);
    const roles =
      getUpdatedProjectTeamRoles(fieldsValue, project.projectRoles) || [];

    if (
      _.isEqual(roles, project.projectRoles) === false ||
      _.isEqual(fieldsValue.projectViewers, project?.projectViewers === false)
    ) {
      showConfirm();
    } else {
      setShowCopyModal(true);
    }
  };

  const showConfirm = () => {
    confirm({
      title: intl.formatMessage({ id: "general.unsavedChangesTitle" }),
      content: intl.formatMessage({ id: "general.unsavedChangesOverwrite" }),
      onOk() {
        setShowCopyModal(true);
      },
    });
  };

  if (!regions) {
    return (
      <>
        <Form form={form} component={false} />
        <Spin />
      </>
    );
  }

  const tabItems = Object.keys(regions).map((regionId) => ({
    key: regionId,
    label: (regions || {})[regionId]?.fallback,
    children: (
      <RegionDisplayTab
        key={regionId}
        regionId={regionId}
        regionTeamRoles={
          projectTeamRoles?.filter(
            (projectTeamRole) => projectTeamRole.regionId === regionId
          ) || []
        }
        form={form}
        editMode={
          canUser(ProjectUserPermissions.EditProjectRoles) && !readOnlyMode
        }
      />
    ),
  }));

  tabItems.push({
    key: "projectViewers",
    label: intl.formatMessage({ id: "project.projectViewers" }),
    children: (
      <ProjectViewersTab
        projectViewers={projectViewers}
        readOnlyMode={readOnlyMode}
      />
    ),
  });

  return (
    <PageWrapper
      pageTitle={intl.formatMessage({ id: "project.title" })}
      backButtonRoute={routes.projects.get(projectId || "")}
      pageActions={
        <StickyActionsHeader
          rightChildren={
            !readOnlyMode && (
              <>
                <Button
                  data-test-id="copy-project-team-button"
                  disabled={
                    !canUser(ProjectUserPermissions.EditProjectRoles) ||
                    project.isMigrated
                  }
                  onClick={() => validateUnsavedChangesAndSetShowCopyModal()}
                >
                  {intl.formatMessage({
                    id: "project.copyProjectTeam",
                  })}
                </Button>
                <SaveAndCancel
                  pageName="project-team-editor"
                  cancelOnClick={(): void =>
                    navigate(routes.projects.get(projectId || ""))
                  }
                  saveOnClick={saveProjectTeam}
                  loading={updatingProjectTeam}
                  saveDisabled={
                    !canUser(ProjectUserPermissions.EditProjectRoles)
                  }
                />
              </>
            )
          }
        />
      }
    >
      <Row gutter={GUTTER_SIZE.large}>
        <Col flex="auto">
          <Card
            title={
              <Space>
                {intl.formatMessage(
                  { id: "project.projectTeamTitle" },
                  {
                    projectName: project.projectName,
                    projectNumber: project.projectNumber,
                  }
                )}
              </Space>
            }
          >
            <RoleForm form={form} layout="vertical" autoComplete="off">
              <Tabs defaultActiveKey={defaultTab} centered items={tabItems} />
            </RoleForm>
          </Card>
        </Col>
      </Row>

      <TeamCopyModal
        modalOpen={showCopyModal}
        setShowCopyModal={setShowCopyModal}
        project={project}
        refetchProject={refetchProject}
      />
    </PageWrapper>
  );
};
